import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ColumnDef } from "./types";

interface DataTableSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

interface DataTableEmptyState {
  icon?: LucideIcon;
  title: string;
  description?: string;
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  getRowId: (row: T) => string;
  isLoading?: boolean;
  emptyState: DataTableEmptyState;
  search?: DataTableSearch;
  filters?: ReactNode;
  toolbarRight?: ReactNode;
  pagination?: DataTablePagination;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  isLoading,
  emptyState,
  search,
  filters,
  toolbarRight,
  pagination,
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  const showToolbar = !!search || !!filters || !!toolbarRight;
  const EmptyIcon = emptyState.icon;

  return (
    <div className="space-y-4">
      {showToolbar && (
        <div className="flex flex-col sm:flex-row gap-4">
          {search && (
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={search.placeholder}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                className="pl-10 bg-slate-50 rounded-2xl"
              />
            </div>
          )}
          {filters}
          {toolbarRight}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          {EmptyIcon && <EmptyIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />}
          <p>{emptyState.title}</p>
          {emptyState.description && (
            <p className="text-sm mt-2 text-slate-400">{emptyState.description}</p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={`text-xs font-bold text-slate-500 uppercase tracking-wide ${
                      column.align === "right" ? "text-right" : ""
                    } ${column.headerClassName ?? ""}`}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow
                  key={getRowId(row)}
                  className={`border-t border-slate-100 ${onRowClick ? "cursor-pointer hover:bg-slate-50/50" : ""} ${rowClassName?.(row) ?? ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={`${column.align === "right" ? "text-right" : ""} ${column.cellClassName ?? ""}`}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {`Showing ${(pagination.page - 1) * pagination.pageSize + 1} to ${Math.min(
              pagination.page * pagination.pageSize,
              pagination.total
            )} of ${pagination.total} results`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-2xl"
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {"Previous"}
            </Button>
            <div className="text-sm font-bold text-slate-700">
              {`Page ${pagination.page} of ${pagination.totalPages}`}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-2xl"
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
            >
              {"Next"}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
